import React from 'react';
import { useI18n } from '../../i18n';
import { FaTimes, FaFile, FaFilePdf, FaFileImage, FaFileAlt } from 'react-icons/fa';

const formatSize = (bytes) => {
    if (bytes >= 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    if (bytes >= 1024) return (bytes / 1024).toFixed(0) + ' KB';
    return bytes + ' B';
};

const FileIcon = ({ mimeType }) => {
    if (!mimeType) return <FaFile />;
    if (mimeType.startsWith('image/')) return <FaFileImage style={{ color: '#0C5097' }} />;
    if (mimeType === 'application/pdf') return <FaFilePdf style={{ color: '#e53e3e' }} />;
    if (mimeType.includes('word') || mimeType.includes('text')) return <FaFileAlt style={{ color: '#2b6cb0' }} />;
    return <FaFile style={{ color: '#718096' }} />;
};

/**
 * Attachment chip displayed inside the composer footer area.
 *
 * Props:
 *  - name (string)        — original filename
 *  - size (number)        — bytes
 *  - mimeType (string)    — MIME type
 *  - progress (number)    — 0-100, or null when complete
 *  - error (string|null)  — error message if upload failed
 *  - onRemove (fn)        — called when user clicks ✕
 */
const EmailAttachmentChip = ({ name, size, mimeType, progress, error, onRemove }) => {
    const { t } = useI18n();
    const isUploading = progress !== null && progress < 100;
    const isDone = progress === 100 || progress === null;

    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '5px 10px',
            borderRadius: 6,
            border: error
                ? '1px solid #e53e3e'
                : isUploading
                    ? '1px solid #bee3f8'
                    : '1px solid #CBD5E0',
            background: error ? '#fff5f5' : isUploading ? '#ebf8ff' : '#F7FAFC',
            maxWidth: 220,
            position: 'relative',
            overflow: 'hidden',
            flexShrink: 0,
        }}>
            {/* Progress bar underlay */}
            {isUploading && (
                <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: `linear-gradient(to right, rgba(12,80,151,0.08) ${progress}%, transparent ${progress}%)`,
                    pointerEvents: 'none',
                }} />
            )}

            <FileIcon mimeType={mimeType} />

            <div style={{ flex: 1, minWidth: 0 }}>
                <div className="ui-font-11" style={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontWeight: 600,
                    color: error ? '#c53030' : '#2D3748',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}>
                    {name}
                </div>
                <div className="ui-font-10" style={{
                    fontFamily: 'Montserrat, sans-serif',
                    color: error ? '#c53030' : '#718096',
                }}>
                    {error
                        ? error
                        : isUploading
                            ? `${t('pages.email.attachments.uploading')} ${progress}%…`
                            : formatSize(size)}
                </div>
            </div>

            <button
                type="button"
                onClick={onRemove}
                title={t('pages.email.attachments.remove')}
                style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 2,
                    color: '#A0AEC0',
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                }}
            >
                <FaTimes size={10} />
            </button>
        </div>
    );
};

export default EmailAttachmentChip;
